import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Save, Edit3, CheckCircle2 } from "lucide-react";
import { RegulationAttachments } from "@/components/RegulationAttachments";

/** Very light Markdown-ish renderer for our regulation content: # ## ### **bold** *italic* > blockquote - bullets. */
function renderMD(md) {
  const out = [];
  const lines = (md || "").split(/\r?\n/);
  let i = 0;
  let bulletBuf = [];
  const flushBullets = () => {
    if (bulletBuf.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-6 space-y-1 my-2 text-white/85">{bulletBuf.map((b, k) => <li key={k} dangerouslySetInnerHTML={{ __html: inline(b) }} />)}</ul>);
      bulletBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|\s)\*(.+?)\*(?=\s|[.,;:!?]|$)/g, "$1<em>$2</em>");

  while (i < lines.length) {
    const ln = lines[i];
    if (/^\s*$/.test(ln)) { flushBullets(); i++; continue; }
    if (ln.startsWith("### ")) { flushBullets(); out.push(<h3 key={i} className="font-heading font-black uppercase tracking-tight text-xl mt-6 mb-2 text-[#A855F7]">{ln.slice(4)}</h3>); i++; continue; }
    if (ln.startsWith("## ")) { flushBullets(); out.push(<h2 key={i} className="font-heading font-black uppercase tracking-tighter text-2xl mt-8 mb-3">{ln.slice(3)}</h2>); i++; continue; }
    if (ln.startsWith("# ")) { flushBullets(); out.push(<h1 key={i} className="font-heading font-black uppercase tracking-tighter text-3xl mt-8 mb-4">{ln.slice(2)}</h1>); i++; continue; }
    if (ln === "---") { flushBullets(); out.push(<hr key={i} className="my-6 border-white/10" />); i++; continue; }
    if (ln.startsWith("> ")) { flushBullets(); out.push(<blockquote key={i} className="border-l-2 border-[#A855F7] pl-4 my-3 text-white/85 italic" dangerouslySetInnerHTML={{ __html: inline(ln.slice(2)) }} />); i++; continue; }
    if (ln.startsWith("- ")) { bulletBuf.push(ln.slice(2)); i++; continue; }
    flushBullets();
    out.push(<p key={i} className="text-white/85 my-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(ln) }} />);
    i++;
  }
  flushBullets();
  return out;
}

export default function RegulationPage() {
  const { user, refresh } = useAuth();
  const [reg, setReg] = useState(null);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const sentinelRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/regulation"); setReg(data); setDraft(data.content_md); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!sentinelRef.current || user?.role !== "client") return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setScrolledEnd(true);
    }, { threshold: 0.5 });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [reg, edit, user?.role]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/regulation", { content_md: draft });
      setReg(data);
      setEdit(false);
      toast.success("Regolamento aggiornato — tutti i clienti dovranno accettare la nuova versione");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post("/regulation/accept");
      toast.success("Grazie, regolamento accettato");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAccepting(false); }
  };

  if (!reg) return <div className="text-white/50 label-caps">Caricamento…</div>;

  const isAdmin = user?.role === "admin";
  const isClient = user?.role === "client";
  const alreadyAccepted = isClient && user?.regulation_accepted_version === reg.version;

  return (
    <div className="max-w-3xl">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-caps">Regolamento e Listino</div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mt-1">Regolamento</h2>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mt-2">
            Versione {reg.version} · aggiornato {new Date(reg.updated_at).toLocaleDateString("it-IT")}
          </div>
        </div>
        {isAdmin && !edit && (
          <button data-testid="reg-edit-btn" onClick={() => setEdit(true)} className="btn-primary flex items-center gap-2"><Edit3 size={14} /> Modifica</button>
        )}
      </div>


      {isClient && alreadyAccepted && (
        <div data-testid="reg-accepted-badge" className="mb-4 border-l-4 border-[#10B981] bg-[#10B981]/10 p-3 text-sm text-white/90 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-[#10B981]" /> Hai accettato questa versione del regolamento.
        </div>
      )}

      {isClient && !alreadyAccepted && (
        <div data-testid="reg-not-accepted-banner" className="mb-4 border-l-4 border-[#FF3366] bg-[#FF3366]/10 p-3 text-sm text-white/90">
          Per poter prenotare devi leggere e accettare il regolamento fino in fondo.
        </div>
      )}

      {edit ? (
        <div>
          <textarea data-testid="reg-editor" rows={30} value={draft} onChange={e => setDraft(e.target.value)}
            className="w-full bg-transparent border border-white/15 focus:border-[#A855F7] outline-none text-white p-3 font-mono text-sm" />
          <div className="mt-3 flex gap-2">
            <button data-testid="reg-save-btn" disabled={saving} onClick={save} className="btn-primary flex items-center gap-2"><Save size={14} /> {saving ? "Salvo…" : "Salva nuova versione"}</button>
            <button onClick={() => { setEdit(false); setDraft(reg.content_md); }} className="btn-ghost">Annulla</button>
          </div>
        </div>
      ) : (
        <div data-testid="reg-content" className="card-surface p-4 md:p-6">
          {renderMD(reg.content_md)}
          <div ref={sentinelRef} data-testid="reg-scroll-sentinel" className="h-1" />
        </div>
      )}

      {!edit && <RegulationAttachments reg={reg} onChange={setReg} isAdmin={isAdmin} />}

      {isClient && !edit && !alreadyAccepted && (
        <div className="mt-6 sticky bottom-24 z-10">
          <button
            data-testid="reg-accept-btn"
            onClick={accept}
            disabled={!scrolledEnd || accepting}
            className={`btn-primary w-full flex items-center justify-center gap-2 ${!scrolledEnd ? "!bg-[#2A2233] !text-white/70 cursor-not-allowed" : ""}`}>
            <CheckCircle2 size={16} /> {accepting ? "Accetto…" : (scrolledEnd ? "Accetta" : "Scorri fino in fondo per accettare")}
          </button>
        </div>
      )}
    </div>
  );
}
